import type {
  PostgrestError,
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import type { OrderStageReference } from '../pages/event/order/OrderStageTimeline';
import { recordRealtimeEvent } from '../lib/realtimeTelemetry';
import { isMissingColumnError } from '../utils/supabaseErrors';
import type { EventInvoiceReviewGate } from './invoiceWorkflow';

type ClientOption = { supabase?: SupabaseClient };
type EventAuditMetadata = {
  updated_at?: string | null;
  updated_by?: string | null;
};

export type EventLogisticsMeta = {
  logistics_delivery_mode_id: string | null;
  logistics_pickup_mode_id: string | null;
  logistics_item_id: string | null;
  logistics_event_venue_id: string | null;
  logistics_distance_miles: number | null;
  logistics_drive_minutes: number | null;
  logistics_distance_source: string | null;
  logistics_distance_computed_at: string | null;
  logistics_distance_route_key: string | null;
};

type OrderStageRow = {
  status: string | null;
  stage_index: number | string | null;
  reference_number: number | string | null;
  label: string | null;
  description: string | null;
  is_terminal: boolean | null;
};

type EventLogisticsRowRecord = {
  logistics_delivery_mode_id: string | number | null;
  logistics_pickup_mode_id: string | number | null;
  logistics_item_id: string | number | null;
  logistics_event_venue_id: string | number | null;
  logistics_distance_miles?: number | string | null;
  logistics_drive_minutes?: number | string | null;
  logistics_distance_source?: string | null;
  logistics_distance_computed_at?: string | null;
  logistics_distance_route_key?: string | null;
  status?: string | null;
  site_location_id?: string | number | null;
  event_code?: string | null;
  created_at?: string | null;
};

const normalizeNullableId = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number') return String(value);
  return null;
};

const normalizeNullableNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeNullableInteger = (value: unknown): number | null => {
  const num = normalizeNullableNumber(value);
  if (num == null) return null;
  return Math.round(num);
};

const normalizeNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isOrderStageRow = (value: unknown): value is OrderStageRow =>
  typeof value === 'object' && value !== null && 'status' in (value as Record<string, unknown>);

const toEventLogisticsMeta = (
  value: unknown,
): { logistics: EventLogisticsMeta } | null => {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  return {
    logistics: {
      logistics_delivery_mode_id: normalizeNullableId(
        (record as EventLogisticsRowRecord).logistics_delivery_mode_id ?? null,
      ),
      logistics_pickup_mode_id: normalizeNullableId(
        (record as EventLogisticsRowRecord).logistics_pickup_mode_id ?? null,
      ),
      logistics_item_id: normalizeNullableId((record as EventLogisticsRowRecord).logistics_item_id ?? null),
      logistics_event_venue_id: normalizeNullableId(
        (record as EventLogisticsRowRecord).logistics_event_venue_id ?? null,
      ),
      logistics_distance_miles: normalizeNullableNumber(
        (record as EventLogisticsRowRecord).logistics_distance_miles ?? null,
      ),
      logistics_drive_minutes: normalizeNullableInteger(
        (record as EventLogisticsRowRecord).logistics_drive_minutes ?? null,
      ),
      logistics_distance_source: normalizeNullableString(
        (record as EventLogisticsRowRecord).logistics_distance_source ?? null,
      ),
      logistics_distance_computed_at: normalizeNullableString(
        (record as EventLogisticsRowRecord).logistics_distance_computed_at ?? null,
      ),
      logistics_distance_route_key: normalizeNullableString(
        (record as EventLogisticsRowRecord).logistics_distance_route_key ?? null,
      ),
    },
  };
};

const mapStageRows = (rows: OrderStageRow[]): OrderStageReference[] =>
  rows
    .filter(isOrderStageRow)
    .map(row => ({
      status: String(row.status ?? ''),
      stage_index: Number(row.stage_index ?? 0),
      reference_number: Number(row.reference_number ?? 0),
      label: String(row.label ?? ''),
      description: row.description != null ? String(row.description) : null,
      is_terminal: Boolean(row.is_terminal),
    }));

function readEventStatusAndMeta(value: unknown): {
  eventStatus: string | null;
  site_location_id: string | null;
  event_code: string | null;
  created_at: string | null;
} {
  if (typeof value !== 'object' || value === null) {
    return { eventStatus: null, site_location_id: null, event_code: null, created_at: null };
  }
  const r = value as EventLogisticsRowRecord & Record<string, unknown>;
  const readStr = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : null);
  const readId = (v: unknown) => {
    if (typeof v === 'string' && v.length > 0) return v;
    if (typeof v === 'number') return String(v);
    return null;
  };
  return {
    eventStatus: readStr(r.status) ?? 'quote',
    site_location_id: readId(r.site_location_id) ?? null,
    event_code: readStr(r.event_code) ?? null,
    created_at: readStr(r.created_at) ?? null,
  };
}

export type OrderStageBundleData = {
  operationalContext?: { sourceStage: string | null; boundaryReason: string | null } | null;
  operationalContextUnavailable?: boolean;
  stageRows: OrderStageReference[];
  eventStatus: string | null;
  eventLogistics: EventLogisticsMeta | null;
  site_location_id: string | null;
  event_code: string | null;
  event_created_at: string | null;
  /** Reason for Lost/Cancelled; set when status is cancelled or void. */
  lost_cancelled_reason: string | null;
  /** Optional terminal-decision context retained with the event. */
  lost_reason_comment: string | null;
  budgetary_quote_submitted_at: string | null;
  status_before_terminal: string | null;
};

const eventStageBundleBaseSelect =
  'status,site_location_id,event_code,created_at,logistics_delivery_mode_id,logistics_pickup_mode_id,logistics_item_id,logistics_event_venue_id,logistics_distance_miles,logistics_drive_minutes,logistics_distance_source,logistics_distance_computed_at,logistics_distance_route_key,lost_cancelled_reason,lost_reason_comment';
const eventStageBundleSelect =
  `${eventStageBundleBaseSelect},budgetary_quote_submitted_at,status_before_terminal`;

function isMissingOptionalEventStageColumn(error: unknown): boolean {
  return isMissingColumnError(error, 'budgetary_quote_submitted_at')
    || isMissingColumnError(error, 'status_before_terminal');
}

export async function fetchOrderStageBundle(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: OrderStageBundleData; error: PostgrestError | null }> {
  const emptyBundle = (): OrderStageBundleData => ({
    stageRows: [],
    eventStatus: null,
    eventLogistics: null,
    site_location_id: null,
    event_code: null,
    event_created_at: null,
    lost_cancelled_reason: null,
    lost_reason_comment: null,
    budgetary_quote_submitted_at: null,
    status_before_terminal: null,
  });
  if (!eventId) {
    return { data: emptyBundle(), error: null };
  }
  const supabase = options?.supabase ?? getSupabaseClient();
  const [stageRes, initialEventRes, operationalRes] = await Promise.all([
    supabase
      .from('event_status_reference')
      .select('status,stage_index,reference_number,label,description,is_terminal')
      .order('stage_index', { ascending: true }),
    supabase
      .from('events')
      .select(eventStageBundleSelect)
      .eq('id', eventId)
      .maybeSingle(),
    supabase.rpc('get_event_operational_stage_context', { p_event_id: eventId }),
  ]);
  const eventRes = initialEventRes.error && isMissingOptionalEventStageColumn(initialEventRes.error)
    ? await supabase
      .from('events')
      .select(eventStageBundleBaseSelect)
      .eq('id', eventId)
      .maybeSingle()
    : initialEventRes;
  if (stageRes.error) return { data: emptyBundle(), error: stageRes.error };
  if (eventRes.error) return { data: emptyBundle(), error: eventRes.error };

  const stageRows = mapStageRows((Array.isArray(stageRes.data) ? stageRes.data : []) as OrderStageRow[]);
  const eventMeta = toEventLogisticsMeta(eventRes.data);
  const { eventStatus, site_location_id, event_code, created_at: event_created_at } = readEventStatusAndMeta(eventRes.data);
  const rawReason = eventRes.data && typeof eventRes.data === 'object' && eventRes.data !== null
    ? (eventRes.data as { lost_cancelled_reason?: unknown }).lost_cancelled_reason
    : undefined;
  const lost_cancelled_reason = typeof rawReason === 'string' ? rawReason : null;
  const eventRow = eventRes.data && typeof eventRes.data === 'object' && eventRes.data !== null
    ? eventRes.data as Record<string, unknown>
    : {};
  const readNullableString = (value: unknown) => typeof value === 'string' ? value : null;

  return {
    data: {
      stageRows,
      operationalContext: operationalRes.data && typeof operationalRes.data === 'object'
        ? {
          sourceStage: normalizeNullableString(operationalRes.data.source_stage),
          boundaryReason: normalizeNullableString(operationalRes.data.boundary?.reason),
        }
        : null,
      operationalContextUnavailable: Boolean(operationalRes.error),
      eventStatus,
      eventLogistics: eventMeta?.logistics ?? null,
      site_location_id,
      event_code,
      event_created_at,
      lost_cancelled_reason,
      lost_reason_comment: readNullableString(eventRow.lost_reason_comment),
      budgetary_quote_submitted_at: readNullableString(eventRow.budgetary_quote_submitted_at),
      status_before_terminal: readNullableString(eventRow.status_before_terminal),
    },
    error: null,
  };
}

const TERMINAL_STAGES_REQUIRING_REASON = ['cancelled', 'lost', 'void'] as const;

/** What to tell the person whose move to Invoiced is waiting on the salesperson. */
export function invoiceReviewHoldMessage(review: EventInvoiceReviewGate): string {
  const name = review.reviewerName || 'the salesperson';
  return review.outcome === 'pending'
    ? `Still waiting on ${name}'s invoice review. The event moves to Invoiced when they approve.`
    : `Sent to ${name} for invoice review. The event moves to Invoiced when they approve.`;
}

/**
 * Moves an event to a stage. A move to Invoiced first asks whether the event's salesperson must
 * review it; when they must, nothing is invoiced and `invoiceReview` says who it waits on.
 */
export async function updateEventStageStatus(params: {
  eventId: string;
  status: string;
  /** Required when moving to Lost or Cancelled; stored on events.lost_cancelled_reason. */
  reason?: string | null;
  /** Optional context retained separately from the required terminal reason. */
  notes?: string | null;
  supabase?: SupabaseClient;
}): Promise<{
  data: { eventStatus: string; invoiceReview?: EventInvoiceReviewGate } | null;
  error: PostgrestError | null;
}> {
  if (String(params.status).trim().toLowerCase() === 'pending_acceptance') {
    return {
      data: null,
      error: {
        name: 'PendingAcceptanceRequiresClientInvite',
        message: 'Pending Acceptance can only be entered after a client portal invite is delivered.',
        details: '',
        hint: 'Open Send to Client and deliver at least one non-budgetary invite.',
        code: 'pending_acceptance_requires_client_invite',
      } as PostgrestError,
    };
  }
  const supabase = params.supabase ?? getSupabaseClient();
  const normalizedStatus = String(params.status).trim().toLowerCase();
  const isTerminal = TERMINAL_STAGES_REQUIRING_REASON.includes(normalizedStatus as 'cancelled' | 'lost' | 'void');
  const payload: Record<string, unknown> = { status: params.status };
  if (isTerminal) {
    payload.lost_cancelled_reason = params.reason != null && String(params.reason).trim() !== ''
      ? String(params.reason).trim()
      : null;
    payload.lost_reason_comment = params.notes != null && String(params.notes).trim() !== ''
      ? String(params.notes).trim()
      : null;
  } else {
    payload.lost_cancelled_reason = null;
    payload.lost_reason_comment = null;
  }
  if (params.status === 'invoiced') {
    const { generateEventInvoices, requestEventInvoiceReview } = await import('./invoiceWorkflow');
    const review = await requestEventInvoiceReview(params.eventId, { supabase });
    if (review.error) return { data: null, error: review.error };
    if (review.data && review.data.outcome !== 'clear') {
      return { data: { eventStatus: review.data.eventStatus, invoiceReview: review.data }, error: null };
    }
    const generated = await generateEventInvoices(
      { eventId: params.eventId, post: true },
      { supabase },
    );
    if (generated.error) return { data: null, error: generated.error };
    if (generated.data.length === 0) {
      return {
        data: null,
        error: {
          name: 'InvoiceGenerationError',
          message: 'No billable invoice lines were generated for this event.',
          details: '',
          hint: '',
          code: 'event_invoice_empty',
        } as PostgrestError,
      };
    }
  }
  const { data, error } = await supabase
    .from('events')
    .update(payload)
    .eq('id', params.eventId)
    .select('status')
    .maybeSingle();
  if (error) return { data: null, error };
  const eventStatus = data?.status != null ? String(data.status) : params.status;
  return { data: { eventStatus }, error: null };
}

/** Update only the Lost/Cancelled reason. */
export async function updateEventLostCancelledReason(params: {
  eventId: string;
  reason: string | null;
  supabase?: SupabaseClient;
}): Promise<{ data: EventAuditMetadata | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();
  const { data, error } = await supabase
    .from('events')
    .update({
      lost_cancelled_reason: params.reason?.trim() || null,
      updated_by: profileId || null,
    })
    .eq('id', params.eventId)
    .select('updated_at,updated_by')
    .maybeSingle();
  return { data: (data as EventAuditMetadata | null) ?? null, error: error ?? null };
}

type StageRealtimeHandlers = {
  onEventStatus?: (eventStatus: string | null) => void;
  onEventLogistics?: (logistics: EventLogisticsMeta | null) => void;
};

export function subscribeToOrderStageEvents(params: {
  eventId: string;
  supabase?: SupabaseClient;
} & StageRealtimeHandlers): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`events-stage:${params.eventId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'events', filter: `id=eq.${params.eventId}` },
      payload => {
        recordRealtimeEvent({ source: 'order-stage-events', table: 'events', eventType: payload.eventType });
        const newRow = (payload as RealtimePostgresChangesPayload<EventLogisticsRowRecord>).new;
        if (newRow && typeof newRow === 'object') {
          const { eventStatus } = readEventStatusAndMeta(newRow);
          if (eventStatus != null) {
            params.onEventStatus?.(eventStatus);
          }
          const parsed = toEventLogisticsMeta(newRow);
          if (parsed) {
            params.onEventLogistics?.(parsed.logistics);
          }
        }
      },
    )
    .subscribe();

  return () => {
    try {
      channel.unsubscribe();
    } catch {
      /* noop */
    }
  };
}
